import { useState } from 'react'

import { UpdateOptOutReasonRequest, updateOptOutReason } from 'core'
import { OptOutReasons } from 'core/application/account/optOutReasons'

import { Alert, Icon, Modal, Notification, SimpleRadio as Radio } from 'common/components'
import { formatYear, parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'

type OptedOutReasonModalProps = {
  visible: boolean
  benefitEligibilityDate: string
  maxDateToTakeBenefitParticipationDecision: string
  planPeriodEndDate: string
  hasBenefitStarted: boolean
  onCancel?: () => void
  planPeriodId: string
  companyUserId: string
}

export default function OptedOutReasonModal({
  visible,
  benefitEligibilityDate,
  hasBenefitStarted,
  maxDateToTakeBenefitParticipationDecision,
  planPeriodEndDate,
  planPeriodId,
  companyUserId,
  onCancel,
}: OptedOutReasonModalProps) {
  const { sendCommand: sendUpdateOptOutReason, isLoading: isUpdateOptOutReasonLoading } = useCommandRequestV2(
    (request: UpdateOptOutReasonRequest) => updateOptOutReason(request),
  )

  const [optOutReason, setOptOutReason] = useState(OptOutReasons.HaveOtherHealthCoverage)

  const handleSubmit = async () => {
    const result = await sendUpdateOptOutReason({
      planPeriodId,
      optOutReason,
      companyUserId,
    })
    if (result.completed) {
      Notification.open({
        message: 'Reason submitted successfully',
        type: 'success',
      })
      window.location.reload()
    }
  }

  return (
    <>
      <Modal
        visible={visible}
        title="Why did you choose to opt out?"
        okText="Submit"
        onOk={handleSubmit}
        okButtonProps={{ loading: isUpdateOptOutReasonLoading }}
        onCancel={onCancel}
      >
        {!hasBenefitStarted && (
          <Alert
            type="warning"
            className="flex flex__space_between_baseline  mb-16"
            showIcon
            icon={<Icon className="fas fa-exclamation-triangle" />}
            message={
              <div>
                You have opted-out of your {formatYear(planPeriodEndDate)} ICHRA benefit. You have until{' '}
                {parseZoneFormat(maxDateToTakeBenefitParticipationDecision)} to change your decision. Once past this
                date, you will not be able to opt-in until the next benefit year unless you have a{' '}
                <a href="https://www.peoplekeep.com/blog/what-is-a-qualifying-life-event" target="_blank">
                  Qualifying Life Event
                </a>
                .
              </div>
            }
          />
        )}

        {hasBenefitStarted && (
          <Alert
            type="warning"
            className="flex flex__space_between_baseline  mb-16"
            showIcon
            icon={<Icon className="fas fa-exclamation-triangle" />}
            message={<div>You have opted-out of your {formatYear(planPeriodEndDate)} ICHRA benefit. </div>}
          />
        )}

        <p className="text-bold">Choose a reason that made you opt-out</p>
        <Radio.Group
          defaultValue={OptOutReasons.HaveOtherHealthCoverage}
          onChange={(e) => {
            setOptOutReason(e.target.value)
          }}
        >
          <Radio className="mb-16" value={OptOutReasons.HaveOtherHealthCoverage}>
            I have other health coverage (Spouse's employer-sponsored group plan, retirement benefits, TRICARE,
            Medicaid, ministry sharing plan, etc.)
          </Radio>
          <Radio className="mb-16" value={OptOutReasons.DontHaveIndividualInsurance}>
            I don’t have individual insurance
          </Radio>
          <Radio className="mb-16" value={OptOutReasons.CoveredByParentsInsurance}>
            I’m covered by my parents' insurance and I don't pay the premium.
          </Radio>
          <Radio className="mb-16" value={OptOutReasons.PlanToTakePremiumTaxCredit}>
            I plan to take premium tax credits for my insurance
          </Radio>
        </Radio.Group>
      </Modal>
    </>
  )
}
